import type React from "react";

import { useStore } from "@/store";
import { Navigate, useLocation } from "react-router-dom";
import { Spinner } from "./ui/spinner";

interface PublicRouteProps {
  children: React.ReactNode;
}

/** What `ProtectedRoute` puts in `Navigate` state when it bounces a deep link to /login. */
interface FromLocationState {
  from?: { pathname?: string; search?: string };
}

/**
 * PublicRoute component that redirects authenticated users away from auth pages.
 * Used for pages like login and signup that should not be accessible to logged-in users.
 *
 * Authenticated users go back to the page they were bounced from, falling back to
 * the dashboard. `ProtectedRoute` has always recorded that origin in `Navigate`
 * state, but nothing read it, so every deep link landed on /dashboard after
 * logging in. That is merely annoying for most routes and breaks /oauth/consent
 * outright: its query string carries the whole authorization request, and losing
 * it strands the user with no way back into the flow.
 */
export const PublicRoute = ({ children }: PublicRouteProps) => {
  const { currentUser, isUserLoading } = useStore();
  const location = useLocation();

  if (isUserLoading) {
    return (
      <div className="flex items-center justify-center h-screen-mobile">
        <Spinner variant="circle" />
      </div>
    );
  }

  if (currentUser) {
    const from = (location.state as FromLocationState | null)?.from;
    // Only ever an in-app path: `from` is a react-router location we created, but
    // it arrives through history state, so refuse anything protocol-relative
    // rather than hand it to Navigate as an open redirect.
    const target =
      from?.pathname && !from.pathname.startsWith("//")
        ? `${from.pathname}${from.search ?? ""}`
        : "/dashboard";
    return <Navigate to={target} replace />;
  }

  // If user is not authenticated, render the public page
  return <>{children}</>;
};
